import type { one_to_one } from '@fireworkhq/proto-contract'
import type { useStoreApi } from '@liveHelper/models/store'
import { actionsSelector, paramsSelector } from '@liveHelper/models/store'
import type { AutomatedMessage } from '@liveHelper/models/store/libs/actions'
import type { LiveHelperParams } from '@liveHelper/params'
import { customLiveHelperEvent } from '@liveHelper/tracking/customLiveHelperEvent'
import { EventType } from '@liveHelper/tracking/eventType'
import type { useTrackNavigateOnCall } from '@liveHelper/tracking/useLiveHelperTracking'
import { startCall } from '@liveHelper/utils/videoCall'
import type {
  Appointment,
  ChatChannelConfig,
} from '@oneToOne/shared/services/rest/types'
import { playNotification } from '@oneToOne/shared/utils/audio'
import { isOnline } from '@oneToOne/shared/utils/widgetAvailability'

import { customEvent, EventType as CoreEventType } from '@core/customEvent'
import { isDebug } from '@core/debug'
import { safeConsoleTable } from '@core/logging/helpers'

let visibilityDebugReason = ''

const isWidgetVisible = (
  response: one_to_one.visitor.ChannelJoinSuccessResponse,
  chatChannelConfig: ChatChannelConfig | undefined,
  appointment: Appointment | undefined,
  params: LiveHelperParams,
): boolean => {
  // widget is always visible when there is active conversation or we are demoing ava
  if (response.conversation || appointment) {
    if (response.conversation) {
      visibilityDebugReason = 'conversation'
    }

    if (appointment) {
      visibilityDebugReason = 'appointment'
    }

    return true
  }

  // Integrator / deep-link forced show (e.g. widget_availability never + URL bypass).
  // Without this, join success overwrites showWidget() because `never` is treated as
  // offline in isOnline() and falls through to visibility false.
  if (params.bypass_widget_availability_hidden) {
    visibilityDebugReason = 'bypass_widget_availability_hidden'
    return true
  }

  // widget is not visible when visibility rules are not matched
  if (!response.visibility_rules_matched) {
    visibilityDebugReason = 'visibility_rules_matched'
    return false
  }

  if (isOnline(chatChannelConfig)) {
    // Automated message will update the visibility of the widget later to true
    if (
      chatChannelConfig?.feature_flags?.qualify_visitor &&
      chatChannelConfig?.config?.restrict_to_qualified_visitors &&
      !params.bypass_qualify_visitors
    ) {
      visibilityDebugReason = 'qualify_visitor'
      return false
    } else {
      /* widget is visible when online and waitlist configuration allows to show widget */
      // waitlist is not enabled
      if (!chatChannelConfig?.config?.waitlist_options?.enabled) {
        visibilityDebugReason = 'waitlist_options not enabled'
        return true
      }

      // waitlist is enabled but waitlist is not full
      if (!response.waitlist_capacity_reached) {
        visibilityDebugReason = 'waitlist_capacity not reached'
        return true
      }

      // waitlist is full but on full action is not hide
      if (
        chatChannelConfig?.config.waitlist_options.on_max_capacity_action !==
        'hide'
      ) {
        visibilityDebugReason = 'on_max_capacity_action not hide'
        return true
      }

      visibilityDebugReason = 'waitlist_capacity_reached'
      return false
    }
  } else {
    if (
      chatChannelConfig?.config?.widget_availability === 'by_office_hours' &&
      !chatChannelConfig?.config.within_office_hours &&
      params.hide_outside_office_hour === true
    ) {
      visibilityDebugReason = 'hide_outside_office_hour'
      return false
    }

    /* widget is visible when offline and have something to show in offline state */

    // there is scheduling page available when offline and it's visible in offline
    if (
      chatChannelConfig?.config?.scheduling_provider !==
        'SCHEDULING_PROVIDER_NONE' &&
      !!chatChannelConfig?.widget_style?.show_offline_scheduling
    ) {
      visibilityDebugReason = 'show_offline_scheduling'
      return true
    }

    // we can show next available office hours and it's visible in offline
    if (
      chatChannelConfig?.config?.widget_availability === 'by_office_hours' &&
      !!chatChannelConfig?.widget_style?.show_offline_office_hours
    ) {
      visibilityDebugReason = 'show_offline_office_hours'
      return true
    }

    visibilityDebugReason = 'offline'
    return false
  }
}

/** Handles the visitor channel join success response. */
export function handleJoinSuccessReply({
  response,
  storeApi,
  trackNavigateOnCall,
  chatChannelConfig,
  appointment,
}: {
  response: one_to_one.visitor.ChannelJoinSuccessResponse
  storeApi: ReturnType<typeof useStoreApi>
  trackNavigateOnCall: ReturnType<typeof useTrackNavigateOnCall>
  chatChannelConfig: ChatChannelConfig | undefined
  appointment: Appointment | undefined
}) {
  const actions = actionsSelector(storeApi.getState())
  const params = paramsSelector(storeApi.getState())
  const send = actions.sendUIStateTransitionEvent
  const videoCallStatus = response?.video_call_status
  const videoCall = response?.video_call
  const conversation = response?.conversation

  switch (videoCallStatus) {
    case 'VIDEO_CALL_STATUS_DIALING': {
      actions.setCallId(videoCall?.id ?? '')
      send({
        type: 'AUTO_DIAL',
        isAppointment: !!appointment,
      })
      break
    }
    case 'VIDEO_CALL_STATUS_RINGING': {
      if (startCall(videoCall, 'audience', storeApi)) {
        playNotification()
        send('RECEIVE_CALL')
      }
      break
    }
    case 'VIDEO_CALL_STATUS_ON_CALL': {
      if (startCall(videoCall, 'host', storeApi)) {
        // We are tracking navigation here because we want to know only
        // when the visitor has been navigated successfully
        trackNavigateOnCall(conversation?.id)
        send('AUTO_START_CALL')
      }
    }
  }
  if (conversation) {
    send('AUTO_START_CHAT')
    actions.setConversation(conversation)
    customLiveHelperEvent({
      type: EventType.FW_LIVE_HELPER_START_CONVERSATION,
      data: {
        widget_id: params?.widget_id,
      },
    })
  }

  if (appointment) {
    send('SHOW_APPOINTMENT')
  }
  // If no need to auto transit to other state, then transit to idle state to
  // update top level state.
  send('AUTO_IDLE')

  const shouldWidgetBeVisible = isWidgetVisible(
    response,
    chatChannelConfig,
    appointment,
    params,
  )

  const chatChannelAutomatedMessage: AutomatedMessage = {
    ...response.automated_message,
    src: 'chat_channel',
  }

  let automatedMessage: AutomatedMessage | undefined = undefined
  if (isOnline(chatChannelConfig) && chatChannelAutomatedMessage?.message) {
    automatedMessage = chatChannelAutomatedMessage
  }

  actions.setWidgetState({
    waitlistCapacityReached: !!response.waitlist_capacity_reached,
    isWidgetVisible: shouldWidgetBeVisible,
    visibilityRulesMatch: !!response.visibility_rules_matched,
    automatedMessage,
  })

  customEvent({
    type: CoreEventType.FW_LIVE_HELPER_READY,
    data: { widget_id: params.widget_id },
  })

  if (isDebug()) {
    safeConsoleTable({
      waitlist_capacity_reached: response.waitlist_capacity_reached,
      visibility_rules_matched: response.visibility_rules_matched,
      appointment: appointment,
      widget_availability: chatChannelConfig?.config?.widget_availability,
      waitlist_options_enabled:
        chatChannelConfig?.config?.waitlist_options?.enabled,
      waitlist_options_on_max_capacity_action:
        chatChannelConfig?.config?.waitlist_options?.on_max_capacity_action,
      scheduling_provider: chatChannelConfig?.config?.scheduling_provider,
      show_offline_scheduling:
        chatChannelConfig?.widget_style?.show_offline_scheduling,
      feature_flags_ava: chatChannelConfig?.feature_flags?.ava,
      show_offline_office_hours:
        chatChannelConfig?.widget_style?.show_offline_office_hours,
      hide_outside_office_hour: params.hide_outside_office_hour,
      automated_message: JSON.stringify(automatedMessage),
      result: `${shouldWidgetBeVisible} - ${visibilityDebugReason}`,
    })
  }
}
